import BaseModel from './BaseModel';
import FileModel from './FileModel';
import VehicleModel from './VehicleModel';
import UserModel from './UserModel';
import { INSPECTION_TYPES, SECTIONS } from './InspectionTemplateModel';

const STATUS_LABELS = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  canceled: 'Canceled',
};

export class InspectionItemModel extends BaseModel {
  init(data) {
    this.id = data.id || 0;
    this.name = data.name || '';
    this.section = data.section || '';
    this.item_type = data.item_type || 'pass_fail';
    this.passed = data.passed ?? null;
    this.value = data.value || '';
    this.notes = data.notes || '';
    this.sort_order = data.sort_order ?? 0;
    this.attachments = data.attachments || [];
    this.attachments_detail = (data.attachments_detail || []).map(a => new FileModel(a));
    this.work_request = data.work_request || null;
    this.work_request_external_id = data.work_request_external_id || null;
  }
}

export default class InspectionModel extends BaseModel {
  init(data) {
    this.id = data.id || 0;
    this.created = data.created ? new Date(data.created) : null;
    this.updated = data.updated ? new Date(data.updated) : null;
    this.vehicle = data.vehicle ? new VehicleModel(data.vehicle) : null;
    this.template = data.template || null;
    this.inspector = data.inspector ? new UserModel(data.inspector) : null;
    this.trip = data.trip || null;
    this.trip_title = data.trip_title || '';
    this.inspection_type = data.inspection_type || '';
    this.status = data.status || 'in_progress';
    this.inspection_date = data.inspection_date || '';
    this.vin = data.vin || '';
    this.odometer = data.odometer ?? null;
    this.notes = data.notes || '';
    this.archived = data.archived || false;
    this.all_passed = data.all_passed ?? null;
    this.items = (data.items || []).map(i => new InspectionItemModel(i));
  }

  get itemsBySection() {
    const grouped = {};
    SECTIONS.forEach((section) => {
      grouped[section.value] = this.items
        .filter(i => i.section === section.value)
        .sort((a, b) => a.sort_order - b.sort_order);
    });
    return grouped;
  }

  get inspectionTypeLabel() {
    const found = INSPECTION_TYPES.find(t => t.value === this.inspection_type);
    return found ? found.label : this.inspection_type;
  }

  get statusLabel() {
    return STATUS_LABELS[this.status] || this.status;
  }

  get inspectorName() {
    return this.inspector ? this.inspector.fullName : '';
  }

  get vehicleName() {
    return this.vehicle ? this.vehicle.display_label : '';
  }

  get vehicleNameSimple() {
    return this.vehicle?.name || '';
  }

  get tripName() {
    return this.trip?.title || this.trip_title || '';
  }

  get templateName() {
    return this.template?.name || '';
  }

  get odometerRequired() {
    return this.template?.odometer_required || false;
  }

  get answeredCount() {
    return this.items.filter(i => (i.item_type === 'pass_fail' ? i.passed !== null : !!i.value)).length;
  }

  get isCompleted() {
    return this.status === 'completed';
  }

  get isCanceled() {
    return this.status === 'canceled';
  }

  // Completed and canceled are both terminal: the API refuses any further edits, so every
  // write affordance in the UI must be gated on this rather than on isCompleted alone.
  get isTerminal() {
    return this.isCompleted || this.isCanceled;
  }

  // Mirrors OPEN_INSPECTION_STATUSES on the API: work that has not finished. Both canceling
  // and editing are permitted in this window.
  get isOpen() {
    return this.status === 'pending' || this.status === 'in_progress';
  }

  // Only ad hoc inspections are canceled on their own. A trip inspection belongs to its trip
  // assignment — saving the trip's assignments recreates it as pending, so a cancel here would
  // not stick — and the API refuses it; the trip's cancel and deny actions cancel it instead.
  get isCancelable() {
    return this.isOpen && !this.isTripInspection;
  }

  // The API rebuilds the checklist from the template, so it only allows the swap while nothing
  // has been recorded against the old one.
  get canChangeTemplate() {
    return this.status === 'pending';
  }

  // A trip assignment owns the vehicle, driver and dates of its inspections, and shares them
  // with that assignment's other inspection, so they are not editable from here.
  get isTripInspection() {
    return !!(this.trip || this.trip_title);
  }

  get failedItems() {
    return (this.items || []).filter(i => i.passed === false);
  }

  get allPassed() {
    const items = this.items || [];
    if (!items.length) return null;
    return items.every(i => i.passed === true);
  }
}
